/**
 * Redux reducer bundle for entries and single entry view on project page
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
import { createSelector } from 'reselect';
import { browserHistory } from 'react-router';
import request from 'axios';
import idx from 'idx';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';

import { ENTRY_SCALE_LEVELS, PAGINATION_PAGE_SIZE, DEFAULT_API_ENTRY_SORTING }
  from 'common/variables';
import {
  getPaginationValues, getFiltersString, getFormattedFilters, cleanupFilters, calculatePageOffset,
} from 'common/utils';
import { getProject, fetchProject } from 'project/ducks/project';
import { getAwarded as getAwardedCreatives } from 'project/ducks/creatives';
import * as commonImports from 'project/ducks/common';
import revisionsReducer, * as revisionImports from 'project/ducks/revisions';
import { getSubcategorySlug } from 'common/ducks/categories';
import { setBannerError } from 'error';
import { getProjectUrl } from 'project/utils';
import { FETCH_SINGLE_SUCCESS } from 'single_entry/ducks';

/* INITIAL STATES
================================================================================================ */
export const initialEntriesState = {
  isFetching: false,
  isFetchingOptions: false,
  isError: null,
  entriesById: {},
  revisionsById: {},
  commentsByRevision: {},
  visibleEntries: [],
  itemsCount: 0,
  pageSize: 0,
  pageCount: 0,
  currentPage: 0,
  nextPage: null,
  prevPage: null,
  currentSelectedEntry: null,
  currentSelectedRevision: null,
  showDeleteModal: false,
  showReinstateModal: false,
  scoredRevision: null,
  feedbackSent: false,
  username: null,
};

export const singleEntryInitialState = {
  scaleLevel: 4,
  zoom: 1,
  maxScale: ENTRY_SCALE_LEVELS,
  currentPage: 0,
};


/* HELPERS
================================================================================================ */
export const DISMISS_FEEDBACK = 'DISMISS_FEEDBACK';
export const dismissFeedback = () => ({ type: DISMISS_FEEDBACK });
export const getProjectEntriesState = state => state.project.projectPage.entries;
export const getProjectEntries = state => getProjectEntriesState(state).entriesById;
export const getEntry = (state, id) => getProjectEntriesState(state).entriesById[id];
export const getEntryModalState = state => state.project.projectPage.entryModal;
export const getOffset = ({ currentPage }) => (calculatePageOffset(currentPage));

const insertEntries = (entry, entries, revisions) => {
  const newEntry = {};

  Object.entries(entry).forEach((e) => {
    const [k, v] = e;
    if (k === 'revisions') {
      if (v.length) {
        const entryRevisions = [];

        v.forEach((rev) => {
          entryRevisions.push(rev.id);
          revisions[rev.id] = rev;
        });

        if (entryRevisions.length > 1) {
          newEntry.hasMultiple = true;
        }

        entryRevisions.sort((a, b) => a < b);
        newEntry.revisions = entryRevisions;
      } else {
        newEntry.revisions = [];
      }
    } else {
      newEntry[k] = v;
    }
  });

  entries[newEntry.id] = newEntry;
};

/* Get project ranked tips, sorted by value */
export const getRankedTips = (state) => {
  const project = getProject(state);
  return sortBy(project.package_tips || [], t => -t.feature.value);
}


/* REDUCERS
================================================================================================ */
export default function entriesReducer(state = initialEntriesState, action) {
  let data;
  let entries;
  let entry;
  let revisions;
  let visibleEntries;

  switch (action.type) {
    // Entries
    case commonImports.FETCH_ENTRIES_REQUEST:
      return {
        ...state,
        isFetching: true,
        isError: false,
        username: action.payload,
      };
    case FETCH_SINGLE_SUCCESS:
      entries = {};
      revisions = {};
      insertEntries(action.payload, entries, revisions);

      return {
        ...state,
        entriesById: { ...state.entriesById, ...entries },
        revisionsById: { ...state.revisionsById, ...revisions },
      };
    case commonImports.FETCH_ENTRIES_SUCCESS:
      data = action.payload;
      entries = {};
      revisions = {};
      visibleEntries = [];

      const { currentPage, pageCount } = getPaginationValues(data);

      data.results.forEach((entr) => {
        insertEntries(entr, entries, revisions);
        visibleEntries.push(entr.id);
      });

      return {
        ...state,
        isFetching: false,
        isError: false,
        entriesById: { ...state.entriesById, ...entries },
        revisionsById: { ...state.revisionsById, ...revisions },
        visibleEntries,
        itemsCount: data.count,
        pageSize: data.limit,
        nextPage: data.next,
        prevPage: data.previous || 0,
        pageCount,
        currentPage,
      };
    case commonImports.FETCH_ENTRIES_FAILURE:
      return {
        ...state,
        isFetching: false,
        isError: true,
      };
    case commonImports.CREATE_ENTRY_SUCCESS:
      data = action.payload;
      entries = {};
      revisions = {};
      visibleEntries = [...state.visibleEntries];

      const itemsCount = state.itemsCount + 1;
      const pageSize = state.pageSize;
      let nextPg = state.nextPage;
      let pgCount = state.pageCount;

      insertEntries(data, entries, revisions);

      /*
       * if we have more entries than we can display, we need to remove
       * the last visible entry and add the new one at the beginning
       * of the visibleEntries array
       */
      if (visibleEntries.length === state.pageSize) {
        visibleEntries.pop();

        // add another page to pager if needed
        if ((itemsCount / pageSize) > pgCount) {
          pgCount += 1;
        }

        // if we have 2 pages, it might've happened that we don't
        // have the `nextPage` defined yet
        if (pgCount === 2 && !nextPg) {
          nextPg = state.pageSize;
        }
      }
      visibleEntries.unshift(data.id);

      return {
        ...state,
        isError: false,
        entriesById: { ...state.entriesById, ...entries },
        revisionsById: { ...state.revisionsById, ...revisions },
        visibleEntries,
        pageCount: pgCount,
        nextPage: nextPg,
      };
    case commonImports.CREATE_ENTRY_FAILURE:
      return {
        ...state,
        isError: true,
      };
    case commonImports.TOGGLE_ENTRY_DELETE:
      return {
        ...state,
        showDeleteModal: !state.showDeleteModal,
        currentSelectedEntry: action.payload,
      };
    case commonImports.TOGGLE_ENTRY_REINSTATE:
      return {
        ...state,
        showReinstateModal: !state.showReinstateModal,
        currentSelectedEntry: action.payload,
      };
    case commonImports.SCORE_REVISION:
      return {
        ...state,
        scoredRevision: action.payload.id,
        feedbackSent: false,
      };

    case DISMISS_FEEDBACK:
      return {
        ...state,
        scoredRevision: null,
        feedbackSent: false,
      };
    case commonImports.REVISION_FEEDBACK_SUCCESS:
      return {
        ...state,
        feedbackSent: true,
      };

    // Revisions
    case commonImports.CREATE_REVISION_SUCCESS:
    case commonImports.CREATE_REVISION_FAILURE:
    case commonImports.SCORE_REVISION_SUCCESS:
    case commonImports.SCORE_REVISION_FAILURE:
    case commonImports.FETCH_COMMENTS:
    case commonImports.FETCH_COMMENTS_SUCCESS:
    case commonImports.FETCH_COMMENTS_FAILURE:
    case commonImports.ADD_COMMENT:
    case commonImports.ADD_COMMENT_SUCCESS:
    case commonImports.ADD_COMMENT_FAILURE:
    case commonImports.ADD_COMMENT_REPLY:
    case commonImports.ADD_COMMENT_REPLY_SUCCESS:
    case commonImports.ADD_COMMENT_REPLY_FAILURE:
      return revisionsReducer(state, action);
    default:
      return state;
  }
}

export const singleEntryReducer = (state = singleEntryInitialState, action) => {
  switch (action.type) {
    case commonImports.ENTRY_ZOOM_IN:
      if (state.scaleLevel < state.maxScale) {
        const newScale = state.scaleLevel + 1;
        const zoom = newScale * 0.25;
        return {
          ...state,
          zoom,
          scaleLevel: newScale,
        };
      }
      return state;
    case commonImports.ENTRY_ZOOM_OUT:
      const newScale = state.scaleLevel - 1;
      if (Math.max(newScale, 0)) {
        const zoom = newScale * 0.25;
        return {
          ...state,
          zoom,
          scaleLevel: newScale,
        };
      }
      return state;
    case commonImports.ENTRY_RESET_ZOOM:
      const defaultState = { ...singleEntryInitialState };

      return {
        ...state,
        scaleLevel: defaultState.scaleLevel,
        zoom: defaultState.zoom,
      };
    default:
      return state;
  }
};


/* ACTION CREATORS
================================================================================================ */
export const zoomInEntry = () => (
  {
    type: commonImports.ENTRY_ZOOM_IN,
  }
);

export const zoomOutEntry = () => (
  {
    type: commonImports.ENTRY_ZOOM_OUT,
  }
);

export const resetZoom = () => (
  {
    type: commonImports.ENTRY_RESET_ZOOM,
  }
);

// Entries/Revisions
const fetchEntriesSuccess = response => (dispatch) => {
  dispatch({
    type: commonImports.FETCH_ENTRIES_SUCCESS,
    payload: response.data,
  });

  return Promise.resolve(response.data.results);
};

const fetchEntriesFailure = error => (dispatch) => {
  dispatch({
    type: commonImports.FETCH_ENTRIES_FAILURE,
    payload: error,
  });

  return Promise.reject(error);
};

export const fetchProjectEntries = (id, offset, limit, username = null) => (dispatch, getState) => {
  const o = offset || 0;
  const l = limit || PAGINATION_PAGE_SIZE;

  const userStr = username ? `&entry_author_username=${username}` : '';

  dispatch({
    type: commonImports.FETCH_ENTRIES_REQUEST,
    payload: username,
  });

  return request.get(
    `/api/v1/projects/${id}/entries/?limit=${l}&offset=${o}${userStr}`
  ).then(resp => dispatch(fetchEntriesSuccess(resp)))
  .catch(error => dispatch(fetchEntriesFailure(error)));
};

export const fetchProjectEntriesIfNeeded = ({
  projectId, projectUrl
}, username) => (dispatch, getState) => {
  const state = getState();
  const project = getProject(state);
  const id = projectId || 0;
  const entriesState = getProjectEntriesState(state);
  const offset = getOffset(entriesState);
  const entries = getProjectEntries(state);

  if (
    project && project.id === id &&
    Object.keys(getProjectEntries(state)).length && username === entriesState.username
  ) {
    return Promise.resolve(entries);
  }

  return dispatch(fetchProjectEntries(id, offset));
};

const createEntrySuccess = (response) => (dispatch) => {
  dispatch({
    type: commonImports.CREATE_ENTRY_SUCCESS,
    payload: response.data,
  });
};

const createEntryFailure = error => ({
  type: commonImports.CREATE_ENTRY_FAILURE,
  payload: error,
});

export const createProjectEntry = (entryData, revisionData, onSuccess, onError) => (dispatch) => {
  dispatch({
    type: commonImports.CREATE_ENTRY_REQUEST,
  });

  /* add revision to data being submitted so that it's also created */
  const data = { ...entryData, revision: revisionData };

  return request.post(`/api/v1/projects/${entryData.project}/entries/`, data)
  .then((resp) => {
    if (onSuccess) { onSuccess(resp); }
    dispatch(createEntrySuccess(resp));
  })
  .catch((error) => {
    if (onError) { onError(error); }
    dispatch(createEntryFailure(error));
  });
};

export const deleteEntry = projectId => (dispatch, getState) => {
  const state = getState();
  const userId = idx(state, _ => _.user.profile_data.id);
  const entryId = getProjectEntriesState(state).currentSelectedEntry;
  const entry = getEntry(state, entryId);
  const isAuthor = entry.author === userId;
  const formData = isAuthor ? { withdrawn: true } : { eliminated: true };

  return request.patch(
    `/api/v1/projects/${projectId}/entries/${entryId}/`,
    { ...formData }
  ).then(({ data }) => {
    /* go to entries or your-entries page, after giving Elastic Search 2s to reflect changes */
    const append = isAuthor? 'your-entries' : 'entries';
    const project = getProject(state);
    const subcategory = getSubcategorySlug(state.categories, project.sub_category);
    setTimeout(() => {
      window.location = getProjectUrl(project, subcategory, append);
    }, 2000);
  }).catch(error => (
    dispatch(
      setBannerError(`Error ${isAuthor? 'withdrawing' : 'eliminating'} entry`, 'Please try again')
    )
  ));
};

export const reinstateEntry = projectId => (dispatch, getState) => {
  const state = getState();
  const userId = idx(state, _ => _.user.profile_data.id);
  const entryId = getProjectEntriesState(state).currentSelectedEntry;
  const entry = getEntry(state, entryId);
  const isAuthor = entry.author === userId;
  const formData = isAuthor ? { withdrawn: false } : { eliminated: false };

  return request.patch(
    `/api/v1/projects/${projectId}/entries/${entryId}/`,
    { ...formData }
  ).then(({ data }) => {
    const append = isAuthor? 'your-entries' : 'entries';
    /* go to entries or your-entries page, after giving Elastic Search 2s to reflect changes */
    const project = getProject(state);
    const subcategory = getSubcategorySlug(state.categories, project.sub_category);
    setTimeout(() => {
      window.location = getProjectUrl(project, subcategory, append);
    }, 2000);
  }).catch(error => (
    dispatch(
      setBannerError(`Error ${isAuthor? 'un-withdrawing' : 'un-eliminating'} entry`, 'Please try again')
    )
  ));
};

export const toggleEntryDelete = entryId => ({
  type: commonImports.TOGGLE_ENTRY_DELETE,
  payload: entryId,
});

export const toggleEntryReinstate = entryId => ({
  type: commonImports.TOGGLE_ENTRY_REINSTATE,
  payload: entryId,
});
